import { useEffect, useMemo, useState } from "react";
import { Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { isPhoneQuery } from "@shared/format/phone";

import type { CommandAction } from "@/components/domain/CommandPalette";
import {
  useCommandPalette,
  useCommandPaletteSearch,
} from "@/components/domain/CommandPaletteProvider";
import { useCustomerSearch } from "@/hooks/useCustomerSearch";
import { isSuperadminRole, useAuth } from "@/lib/auth/AuthContext";

// SUCHE-1 (Marvin 18.09.2026) — die Schnellsuche oben findet Kunden per
// Telefonnummer. Die Palette kennt sonst nur die 40 obersten Kunden der
// Rangliste und filtert deren NAMEN im Browser; eine Nummer fand dort nie
// etwas. Steht eine Telefonnummer im Feld (`isPhoneQuery`), fragt diese
// Komponente dieselbe Server-Suche wie die Kundenliste — also dieselbe
// Vergleichsform («079 …» = «+41 79 …» = «0041 79 …») — und haengt die
// Treffer als eigene Gruppe mit der gespeicherten Nummer daneben ein.
//
// Eine Komponente statt eines Hooks in der App-Huelle: sie liest die Eingabe
// und zeichnet bei jedem Tastendruck neu — die Huelle soll das nicht.

const BUNDLE_TOKEN = "customers-phone";
const DEBOUNCE_MS = 300;
const GROUP = "Kunden mit dieser Nummer";

export function CustomerPhonePaletteSearch(): null {
  const navigate = useNavigate();
  const { registerActions } = useCommandPalette();
  const search = useCommandPaletteSearch();
  const { user } = useAuth();
  // Dieselbe Schranke wie die Kunden-Aktionen (useCustomerPaletteActions).
  const operativ = !!user && !isSuperadminRole(user.role);

  const [query, setQuery] = useState("");
  useEffect(() => {
    const next = operativ && isPhoneQuery(search) ? search.trim() : "";
    const handle = setTimeout(() => setQuery(next), next ? DEBOUNCE_MS : 0);
    return () => clearTimeout(handle);
  }, [search, operativ]);

  const { data } = useCustomerSearch(query, query.length > 0);

  // Stichwort = die getippte Eingabe: der Server hat den Kunden FUER diese
  // Eingabe gefunden, der Name enthaelt die Ziffern aber nicht. So bewertet
  // cmdk den Treffer selbst — er wird gezeigt, gezaehlt («Keine Treffer.»
  // verschwindet) und vorausgewaehlt (Enter oeffnet ihn). Ein «immer
  // zeigen» (forceMount) meldet cmdk NICHT bei seiner Auswahl an: dann war
  // nichts gewaehlt und Enter ging ins Leere (E2E suche1-telefon).
  const actions = useMemo<readonly CommandAction[]>(() => {
    if (query.length === 0 || !data) return [];
    return data.map((customer) => ({
      id: `customer-phone:${customer.id}`,
      label: customer.displayName,
      description: [customer.phone, customer.city].filter(Boolean).join(" · "),
      group: GROUP,
      icon: Phone,
      keywords: [search, customer.id],
      onSelect: () => {
        void navigate(`/kunden/${customer.id}`);
      },
    }));
  }, [data, navigate, query, search]);

  useEffect(() => {
    return registerActions(BUNDLE_TOKEN, actions);
  }, [registerActions, actions]);

  return null;
}
